/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import gql from 'graphql-tag';

import config from 'js/app/config';
import imgix from 'js/lib/imgix';

import type { Theme } from '@coursera/cds-core';
import { Link, Typography, typography2, withTheme } from '@coursera/cds-core';
import { ChevronNextIcon } from '@coursera/cds-icons';
import { Breadcrumb } from '@coursera/coursera-ui';

import { CML } from 'bundles/cml';
import type { CollectionBannerFragment as CollectionBannerFragmentType } from 'bundles/collections/components/__generated__/CollectionBannerFragment';
import getSocialCaptions from 'bundles/collections/utils/getSocialCaptions';
import withFragments from 'bundles/graphql/components/withFragments';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';
import ShareButtonWithModal from 'bundles/sharing-common/components/modal/ShareButtonWithModal';

type PropsFromCDS = {
  theme: Theme;
};
type Props = PropsFromCDS & {
  catalogCollectionPage: CollectionBannerFragmentType;
};

const styles = {
  breadcrumbLink: (theme: Theme) => css`
    color: var(--cds-color-white-0);
  `,
  breadcrumbIcon: css`
    display: flex;
    margin-left: 0.5rem;
    margin-right: 0.5rem;
  `,
  bannerTitleContainer: css`
    display: flex;
    align-items: center;
    margin-bottom: 1rem;

    @media (max-width: 767px) {
      display: block;
    }
  `,
  collectionBanner: (theme: Theme) => css`
    color: var(--cds-color-white-0);
    font-family: 'Source Sans Pro', Arial, sans-serif;
  `,

  desktopBanner: css`
    position: relative;
    height: 100%;

    @media (max-width: 767px) {
      display: none !important;
    }
  `,
  bannerBackgroungImage: css`
    position: absolute;
    top: 0;
    left: 60%;
    background-size: cover;
    width: 40%;
    height: 100%;
  `,
  desktopBannerContainer: css`
    position: absolute;
    background-color: #083e7d;
    top: 0;
    bottom: 0;
    width: 80%;
    height: 100%;
    transform: skewX(-20deg);
    margin-left: -110px;
  `,
  contentContainer: css`
    margin-right: auto;
    margin-left: auto;
    padding-left: 15px;
    padding-right: 15px;

    &::before,
    &::after {
      display: table;
      content: ' ';
    }

    &::after {
      clear: both;
    }

    @media (min-width: 768px) {
      width: 750px;
    }

    @media (min-width: 992px) {
      width: 970px;
    }

    @media (min-width: 1200px) {
      width: 1170px;
    }

    max-width: 975px;
    position: relative;
  `,
  mobileBanner: css`
    background-color: #083e7d;
    display: none !important;

    @media (max-width: 767px) {
      display: block !important;
    }
  `,
  mobileBannerContent: css`
    background-color: #083e7d;
    padding-top: 3rem;
    padding-bottom: 3rem;
    padding-right: 1.5rem;
    padding-left: 1.5rem;
  `,
  cmlComponent: (theme: Theme) => css`
    .rc-CML p {
      font-family: ${typography2.bodySecondary};
    }
  `,
  content: css`
    max-width: 60%;

    @media (max-width: 767px) {
      max-width: 100%;
    }
  `,
};
const IMGIX_PARAMS = {
  dpr: 2,
  fit: 'crop',
  width: 600,
};

const DEFAULT_BANNER_IMAGE = `${config.url.resource_assets}collections/defaultBannerImage.png`;

class CollectionBanner extends React.Component<Props> {
  renderBreadcrumbItem = (path: $TSFixMe, name: $TSFixMe) => {
    const { theme } = this.props;
    return (
      <Breadcrumb.Item>
        <Link
          component={TrackedLink2}
          css={styles.breadcrumbLink(theme)}
          href={path}
          trackingName="breadcrumb_link"
          typographyVariant="subtitleMedium"
          variant="quiet"
          invert
        >
          {name}
        </Link>
      </Breadcrumb.Item>
    );
  };

  renderBreadcrumb = () => {
    const {
      catalogCollectionPage: { slug, title },
    } = this.props;
    return (
      <Breadcrumb
        rootClassName="m-b-2"
        separator={<ChevronNextIcon size="small" color="invert" css={styles.breadcrumbIcon} />}
      >
        {this.renderBreadcrumbItem('/browse', 'Explore')}
        {this.renderBreadcrumbItem(`/collections/${slug}`, title)}
      </Breadcrumb>
    );
  };

  renderContent = () => {
    const {
      theme,
      catalogCollectionPage: { title, description },
    } = this.props;
    const cmlNaptimeShape = description && {
      typeName: 'cml' as const,
      definition: {
        value: description.cml.value,
        dtdId: description.cml.dtdId,
      },
    };

    return (
      <div css={[styles.cmlComponent(theme), styles.content]}>
        {this.renderBreadcrumb()}
        <div css={styles.bannerTitleContainer}>
          <Typography color="invertBody" component="h1" variant="d2semibold">
            {title}
          </Typography>
          <ShareButtonWithModal
            rootClassName="collections-banner-button"
            utmMediumParam="page_share"
            utmContentParam="cc"
            utmCampaignParam="top_button"
            captions={getSocialCaptions()}
          />
        </div>
        <CML
          css={styles.cmlComponent(theme)}
          cml={cmlNaptimeShape}
          shouldRenderMath={false}
          shouldRenderCode={false}
          shouldRenderAssets={false}
        />
      </div>
    );
  };

  render() {
    const {
      theme,
      catalogCollectionPage: { imageUrl },
    } = this.props;

    const processedImageUrl = imgix.processImage(imageUrl || DEFAULT_BANNER_IMAGE, IMGIX_PARAMS);

    return (
      <section css={styles.collectionBanner(theme)}>
        <div css={styles.desktopBanner}>
          <div
            css={styles.bannerBackgroungImage}
            style={{
              backgroundImage: `url(${processedImageUrl})`,
            }}
          />
          <div css={styles.desktopBannerContainer} />
          <div css={styles.contentContainer} className="p-t-2 p-b-3">
            {this.renderContent()}
          </div>
        </div>
        <div css={styles.mobileBanner}>
          <div css={styles.mobileBanner} className="p-x-2 p-y-3">
            {this.renderContent()}
          </div>
        </div>
      </section>
    );
  }
}
const BannerWithTheme = withTheme(CollectionBanner);
const CollectionBannerFragment = gql`
  fragment CollectionBannerFragment on CatalogCollectionPagesV1 {
    slug
    title
    imageUrl
    description {
      ... on CatalogCollectionPagesV1_cmlMember {
        cml {
          value
          dtdId
        }
      }
    }
  }
`;

export default withFragments({
  collectionBannerFragment: CollectionBannerFragment,
})(BannerWithTheme);
